import type { PulseEvent } from '../pulseSchema';
import { PULSE_GA4_ID_TIMEOUT_MS } from '../pulseConstants';
import { GA4_LEGACY_MEASUREMENT_ID } from './ga4Constants';
import { getGa4MeasurementId } from './ga4Tracker';
import { getGtag } from './gtag';

export type Ga4Ids = {
  client_id: string | null;
  session_id: string | null;
};

/** Dedicated GA4 property when configured; otherwise legacy index.html property. */
export const resolveGa4MeasurementIdForIds = (): string =>
  getGa4MeasurementId() || GA4_LEGACY_MEASUREMENT_ID;

const normalizeGtagId = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const EMPTY_IDS: Ga4Ids = { client_id: null, session_id: null };

/**
 * Read GA4 client_id and session_id via gtag get (async callbacks).
 *
 * `gtag('get', ...)` has no timeout of its own and silently never calls back
 * when the property is not configured or consent is denied. Since Pulse awaits
 * this before POSTing, an un-raced promise would stall the send pipeline
 * indefinitely — so the callbacks are always raced against `timeoutMs`.
 */
export const getGa4ClientAndSessionIds = (
  timeoutMs?: number
): Promise<Ga4Ids> => {
  const measurementId = resolveGa4MeasurementIdForIds();
  const gtag = getGtag();
  if (!gtag) return Promise.resolve(EMPTY_IDS);

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const settle = (ids: Ga4Ids): void => {
      if (settled) return;
      settled = true;
      if (timer != null) clearTimeout(timer);
      resolve(ids);
    };

    if (typeof timeoutMs === 'number' && timeoutMs >= 0) {
      timer = setTimeout(() => settle(EMPTY_IDS), timeoutMs);
    }

    try {
      gtag('get', measurementId, 'client_id', (clientId: unknown) => {
        gtag('get', measurementId, 'session_id', (sessionId: unknown) => {
          settle({
            client_id: normalizeGtagId(clientId),
            session_id: normalizeGtagId(sessionId),
          });
        });
      });
    } catch {
      settle(EMPTY_IDS);
    }
  });
};

/** Attach the current GA4 ids to each Pulse event (one gtag lookup per batch). */
export const attachGa4IdsToEvents = async (
  events: PulseEvent[],
  timeoutMs: number = PULSE_GA4_ID_TIMEOUT_MS
): Promise<PulseEvent[]> => {
  if (!events.length) return events;

  const ids = await getGa4ClientAndSessionIds(timeoutMs);
  return events.map((event) => ({
    ...event,
    client_id: ids.client_id,
    session_id: ids.session_id,
  }));
};
